import {Injectable} from '@angular/core';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import {forkJoin, Observable, of} from 'rxjs';
import {catchError, concatMap, filter, map, skip, take, toArray} from 'rxjs/operators';
import {PendingBilling} from 'src/app/models/billing/pending-billing.model';
import {OnSiteStoreDrug} from 'src/app/models/drug/onsite-store-drug.model';
import {AdditionalOrderMessageField} from '../../models/facility/additional-order-message-field.model';
import {MarGroup} from 'src/app/models/facility/mar-group.model';
import {OnsiteStore} from 'src/app/models/facility/onsite-store.model';
import {AdminSchedule} from '../../models/facility/admin-schedule.model';
import {FacilityAccess} from '../../models/facility/facility-access.model';
import {Facility} from '../../models/facility/facility.model';
import {LiteralOrderCategory} from '../../models/literal-order/literal-order-category.model';
import {NursingStationLocation} from '../../models/nursing-station/nursing-station-location.model';
import {NursingStation} from '../../models/nursing-station/nursing-station.model';
import {Physician} from '../../models/physician/physician.model';
import {InvoiceGroup} from '../../models/report/invoice/invoice-group.model';
import {PaginationResult} from '../../models/web/pagination-result.model';
import {UserFacilityAccessMetrics} from '../../models/web/user-facility-access-metrics.model';
import {NotificationService} from '../web/notification.service';
import {ApiResponseService} from './api-response.service';
import {EdpApiService} from './edp-api.service';
import {VisionFacilityService} from './vision-facility.service';
import {BulkFacilityAccess} from '../../models/facility/bulk-facility-access.model';
import {LiteralOrder} from '../../models/literal-order/literal-order.model';
import {EdpURIs} from '../../endpoints/edpURIs';
import {NdcStatusCodes} from '../../models/facility/ndc-status-codes.model';
import {VisionApiURIs} from '../../endpoints/visionApiURIs';
import {VisionApiService} from './vision-api.service';

@Injectable({
  providedIn: 'root'
})
export class FacilityService {


  constructor(
    private api: EdpApiService,
    private webApi: VisionApiService,
    private notificationService: NotificationService,
    private apiResponseService: ApiResponseService,
    private migratedFacilityService: VisionFacilityService
  ) {
  }

  public getFacility(facilityId: string): Observable<Facility> {
    return this.api.get<Facility>(`${EdpURIs.facility()}?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve data for the requested facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getFacilities(includeReadonly: boolean = true): Observable<FacilityAccess[]> {
    return this.api.get<FacilityAccess[]>('/edp/v1/auth/facilities')
      .pipe(
        map(allFacilities =>
          includeReadonly ? allFacilities : allFacilities.filter(facility => facility.accessLevel !== 'READ')),
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of facilities.', 'Error');
          return of(undefined);
        })
      );
  }

  public getActivatedFacilities(includeReadonly: boolean = true): Observable<FacilityAccess[]> {
    const activeFacilities$ = this.migratedFacilityService.getMigratedFacilities()
      .pipe(map(facilities => facilities.filter(facility => facility.active === true)));
    const allFacilities$ = this.getFacilities(includeReadonly);

    return forkJoin([activeFacilities$, allFacilities$])
      .pipe(
        map(([active, all]) => {
          return all.filter(a => active.some(f => f.facilityId === a.facilityId));
        })
      );
  }

  public getUsersWithFacilityAccess(facilityId: string): Observable<FacilityAccess[]> {
    return this.api.get<FacilityAccess[]>(`/edp/v1/auth/facilities?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve users with access to the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getNursingStations(facilityId: string): Observable<NursingStation[]> {
    return this.api.get<NursingStation[]>(`${EdpURIs.facility()}/nursingStations?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of nursing stations within the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public searchPhysicians(lastName: string, facilityId: string, size?: number): Observable<Physician[]> {
    const sizeQuery = size !== undefined && !isNaN(size) ? `?size=${size}` : '';

    return this.api.post<Physician[]>(`/edp/v1/prescribers/search${sizeQuery}`, {lastName, facilityId})
      .pipe(
        catchError(err => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Error searching for physician');
          return of(undefined);
        })
      );
  }

  public getPhysiciansByFacilityIdDataSource(facilityId: string): DataSource {
    return new DataSource({
      pageSize: 50,
      store: new CustomStore({
        key: 'npi',
        load: (loadOptions) => {
          return this.searchPhysicians(loadOptions.searchValue?.substring(0, 16), facilityId, loadOptions.skip + loadOptions.take)
            .pipe(
              concatMap(physicians => physicians),
              skip(loadOptions.skip),
              take(loadOptions.take),
              toArray()
            )
            .toPromise();
        },
        byKey: (npi: string) => {
          if (npi !== '') {
            return this.api.get<Physician>(`/edp/v1/prescribers?npi=${npi}`).toPromise();
          }
        }
      })
    });
  }

  public getLiteralOrderCategories(facilityId: string): Observable<LiteralOrderCategory[]> {
    return this.api.get<LiteralOrderCategory[]>(`/edp/v1/orders/literal/categoryCodes?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the categories for literal orders.', 'Error');
          return of(undefined);
        })
      );
  }

  public getLiteralOrderLibary(facilityId: string, literalCategoryCode: string): Observable<LiteralOrder[]> {
    return this.api.get<LiteralOrder[]>(`/edp/v1/orders/literal/library?facilityId=${facilityId}&literalCategoryCode=${literalCategoryCode}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the literal order library.', 'Error');
          return of(undefined);
        })
      );
  }

  public getAdminTimes(facilityId: string, nursingStationId?: string): Observable<AdminSchedule[]> {
    let url = `${EdpURIs.facility()}/adminTimes?facilityId=${encodeURIComponent(facilityId)}`;
    if (nursingStationId) {
      url += `&nursingStationId=${nursingStationId}`;
    }
    return this.api.get<AdminSchedule[]>(url)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of admin schedules within the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getFacilitiesByName(facName: string): Observable<Facility[]> {
    return this.api.get(`/facilities/${facName}`);
  }

  public grantFacilityAccess(accessModel: FacilityAccess): Observable<any> {
    return this.api.post('/edp/v1/auth/facilities/grant', accessModel)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not grant facility access to user.', 'Error');
          return of(undefined);
        })
      );
  }

  public grantBulkFacilityAccess(accessModel: BulkFacilityAccess): Observable<any> {
    return this.api.post('/edp/v2/auth/facilities/grant', accessModel)
      .pipe(
        catchError((error) => {
          this.notificationService.error('Could not grant bulk facility access to user.', 'Error');
          return of(undefined);
        })
      );
  }

  public revokeFacilityAccess(facilityId: string, userId: string): Observable<any> {
    const body = {
      facilityId: facilityId,
      userId: userId
    };
    return this.api.post('/edp/v1/auth/facilities/revoke', body)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not revoke facility access.', 'Error');
          return of(undefined);
        })
      );
  }

  public getFacilityUserAccessMetrics(): Observable<UserFacilityAccessMetrics[]> {
    return this.api.get<UserFacilityAccessMetrics[]>('/edp/v1/metrics/facilities/access')
      .pipe(
        catchError(() => {
          this.notificationService.error('Facility User Metrics could not be returned.', 'Error');
          return of(undefined);
        })
      );
  }

  public getRooms(facilityId: string, nursingStationId: string): Observable<NursingStationLocation[]> {
    return this.api.get<NursingStationLocation[]>(
      `${EdpURIs.facility()}/rooms?facilityId=${encodeURIComponent(facilityId)}&nursingStationId=${encodeURIComponent(nursingStationId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of rooms within nursing station.', 'Error');
          return of(undefined);
        })
      );
  }

  public getBeds(facilityId: string, nursingStationId: string, room: string): Observable<NursingStationLocation[]> {
    return this.api.get<NursingStationLocation[]>(
      `${EdpURIs.facility()}/beds?facilityId=${encodeURIComponent(facilityId)}&nursingStationId=${encodeURIComponent(nursingStationId)}&roomId=${encodeURIComponent(room)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of beds within the room.', 'Error');
          return of(undefined);
        })
      );
  }

  public getPendingBilling(facilityId: string, patientId: string, invoiceGroupId: string, page: number, size: number): Observable<PaginationResult<PendingBilling[]>> {
    let url = `/edp/v1/billing/pending?facilityId=${encodeURIComponent(facilityId)}&page=${page}&size=${size}`;
    if (patientId) {
      url += `&patientId=${encodeURIComponent(patientId)}`;
    }
    if (invoiceGroupId) {
      url += `&invoiceGroup=${encodeURIComponent(invoiceGroupId)}`;
    }

    return this.api.getPagination<PendingBilling[]>(url)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of pending billing records for the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getInvoiceGroups(facilityId: string): Observable<InvoiceGroup[]> {
    return this.api.get<InvoiceGroup[]>(`/edp/v1/billing/invoices/groups?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve the list of invoice groups for the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getMarGroups(facilityId: string): Observable<MarGroup[]> {
    return this.api.get<MarGroup[]>(`${EdpURIs.facility()}/marGroups?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError(() => {
          this.notificationService.error('Could not retrieve mar groups for the facility.', 'Error');
          return of(undefined);
        })
      );
  }

  public getOnsiteStoreInventory(facilityId: string, ossId: number): Observable<OnSiteStoreDrug[]> {
    return this.api.get<OnSiteStoreDrug[]>(`${EdpURIs.facility()}/onSiteStores/Inventory?facilityId=${encodeURIComponent(facilityId)}&ossId=${ossId}`)
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, `Could not retrieve on site store inventory '${ossId}' for facility.`);
          return of(undefined);
        })
      );
  }

  public getOnsiteStores(facilityId: string): Observable<OnsiteStore[]> {
    return this.api.get<OnsiteStore[]>(`${EdpURIs.facility()}/onSiteStores?facilityId=${encodeURIComponent(facilityId)}`)
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not retrieve on site stores for facility.');
          return of(undefined);
        })
      );
  }

  public getAdditionalOrderMessageFields(facilityId: string): Observable<AdditionalOrderMessageField[]> {
    return this.webApi.get<AdditionalOrderMessageField[]>(VisionApiURIs.getAdditionalOrderMessageFields(encodeURIComponent(facilityId)))
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not retrieve additional order message fields for facility.');
          return of(undefined);
        })
      );
  }

  public addAdditionalOrderMessageFields(field: any): Observable<AdditionalOrderMessageField> {
    return this.webApi.post<AdditionalOrderMessageField>(VisionApiURIs.addAdditionalOrderMessageFields(), field)
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not create an additional order message field.');
          return of(undefined);
        })
      );
  }

  public updateAdditionalOrderMessageFields(field: AdditionalOrderMessageField): Observable<AdditionalOrderMessageField> {
    return this.webApi.put<AdditionalOrderMessageField>(VisionApiURIs.addAdditionalOrderMessageFields(), field)
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not update the additional order message field.');
          return of(undefined);
        })
      );
  }

  public deleteAdditionalOrderMessageFields(fieldId: number): Observable<any> {
    return this.webApi.delete(VisionApiURIs.deleteAdditionalOrderMessageFields(encodeURIComponent(fieldId.toString())))
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not delete additional order message field.');
          return of(undefined);
        })
      );
  }

  public getNdcStatusCodes(facilityId: string): Observable<NdcStatusCodes>{
    return this.api.get<NdcStatusCodes>(EdpURIs.getFacilityNdcStatusCodes(facilityId))
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not retrieve NDC status codes for the facility.');
          return of(undefined);
        })
      );
  }

  public saveNdcStatusCodes(statusCodes: NdcStatusCodes): Observable<any>{
    return this.api.post(EdpURIs.facilityNdcStatusCodes(), statusCodes)
      .pipe(
        catchError((err) => {
          this.apiResponseService.showApiErrorOrDefault(err, 'Could not save NDC status codes for the facility.');
          return of(undefined);
        })
      );
  }
}
